import type { ReactNode } from 'react'
import { LogtoProvider, type LogtoConfig } from '@logto/react'

import { isE2EAuthStub } from './e2e-stub'

interface AuthProviderProps {
  children: ReactNode
}

const endpoint = import.meta.env.VITE_LOGTO_ENDPOINT
const appId = import.meta.env.VITE_LOGTO_APP_ID
const apiResource = import.meta.env.VITE_API_URL

if (!isE2EAuthStub && (!endpoint || !appId)) {
  console.error(
    'Missing VITE_LOGTO_ENDPOINT or VITE_LOGTO_APP_ID. Copy .env.example → ' +
      '.env and fill them in (or set them in your CI environment vars).',
  )
}

const config: LogtoConfig = {
  endpoint: endpoint ?? '',
  appId: appId ?? '',
  // Registering the API as a `resource` is what makes Logto mint a JWT
  // access-token bound to that audience instead of an opaque token. The
  // backend then verifies `aud === LOGTO_RESOURCE` against this same string.
  resources: apiResource ? [apiResource] : [],
  scopes: ['openid', 'profile', 'email'],
}

/**
 * Wraps the app in Logto's provider. Hosted login lives on the Logto
 * endpoint (e.g. auth-staging.aiescapelabs.com); after sign-in Logto
 * redirects to /callback where `useHandleSignInCallback` finishes the
 * code exchange and strips ?code/?state from the URL.
 *
 * When the build-time flag VITE_E2E_AUTH_STUB=1 is set we skip the Logto
 * provider entirely so e2e tests can run without a real Logto deployment.
 * The matching `useUser` / `TokenBridge` branches resolve to stub
 * implementations that report `isAuthenticated=true` with a fake bearer.
 * Production builds never set the flag, so this branch is dead-code-
 * eliminated.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  if (isE2EAuthStub) {
    return <>{children}</>
  }
  return <LogtoProvider config={config}>{children}</LogtoProvider>
}
